import { useRouteError, isRouteErrorResponse, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertTriangle, TrendingUp } from 'lucide-react';
import { Button } from '@/shared/ui/atoms/Button';
import { Badge } from '@/shared/ui/atoms/Badge';
import { ThemeToggle } from '@/shared/ui/molecules/ThemeToggle';
import NotFoundPage from '../pages/NotFoundPage';

/**
 * Route error boundary component for React Router.
 *
 * Automatically displays the minimalist NotFoundPage for 404 HTTP errors,
 * or a clean fallback screen with reload actions for 500 / unexpected errors.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  // If it is a 404 route error, render the dedicated NotFoundPage
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }

  const errorMessage =
    isRouteErrorResponse(error)
      ? error.statusText || (typeof error.data === 'string' ? error.data : error.data?.message) || 'Error al procesar la ruta'
      : error instanceof Error
      ? error.message
      : 'Ocurrió un error inesperado al procesar la solicitud.';

  return (
    <div className="flex min-h-[100dvh] w-full flex-col bg-background text-foreground antialiased selection:bg-primary selection:text-primary-foreground pb-safe">
      {/* Top Navbar */}
      <header className="sticky top-0 z-30 flex flex-col w-full border-b border-border/80 bg-background/80 pt-safe backdrop-blur-md transition-colors">
        <div className="flex h-16 w-full items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="flex items-center gap-2.5 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg px-1 py-0.5 transition-opacity hover:opacity-90"
            aria-label="Cartera"
          >
            <div className="flex size-8 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-xs transition-transform group-hover:scale-105">
              <TrendingUp className="size-4.5 stroke-[2.2]" />
            </div>
            <span className="font-display text-base sm:text-lg font-bold tracking-tight text-foreground">
              Cartera
            </span>
          </Link>
          <ThemeToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md mx-auto flex flex-col items-center text-center space-y-6">
          <Badge
            variant="destructive"
            className="px-3 py-1 font-mono text-xs font-medium uppercase tracking-widest"
          >
            Error del Sistema
          </Badge>

          <div className="flex size-20 items-center justify-center rounded-2xl border border-destructive/20 bg-destructive/10 text-destructive shadow-xs">
            <AlertTriangle className="size-10 stroke-[1.5]" />
          </div>

          <div className="space-y-2">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              Algo salió mal
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {errorMessage}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 w-full pt-2">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="gap-2 cursor-pointer order-2 sm:order-1"
            >
              <ArrowLeft className="size-4" />
              Volver atrás
            </Button>
            <Button
              onClick={() => window.location.reload()}
              className="gap-2 cursor-pointer order-1 sm:order-2"
            >
              <RefreshCw className="size-4" />
              Recargar página
            </Button>
          </div>
        </div>
      </main>

      <footer className="py-4 text-center text-xs text-muted-foreground border-t border-border/40">
        Cartera &copy; {new Date().getFullYear()}
      </footer>
    </div>
  );
}
